// Drills (D-098) : la logique sans écran, reprise de Playbook BCL (« guidages d'exercices ») —
// tirage des signaux, rythme (fixe, accélère, au hasard), compteurs et passage des étapes d'un
// circuit, frise et phases d'un fractionné, textes de résumé. Le temps et le hasard sont injectés :
// tout se teste hors du navigateur (drills.test.ts). Le moteur plein écran vit dans ui/drill-run.ts.
// D-161/D-162 (lot 0) : le drill de réaction a un deuxième mode, « En séquence » (une frise de briques Son, Attente,
// Signal, répétée), et une famille de signaux « Signes » (les gestes du meneur, domain/signs.ts) ; les drills d'avant
// restent « Au hasard », leur réglage ne change pas.
import { DEFAULT_SIGNS, isSignId, signLabel, type SignId } from './signs';

export type DrillKind = 'reaction' | 'circuit' | 'fractionne';
export const KINDS: DrillKind[] = ['reaction', 'circuit', 'fractionne'];
export const KIND_LABEL: Record<DrillKind, string> = { reaction: 'Réaction', circuit: 'Circuit', fractionne: 'Fractionné' };
export const KIND_ICON: Record<DrillKind, string> = { reaction: '⚡', circuit: '🔄', fractionne: '⏳' };

// -----------------------------------------------------------------------------------------------
// Signaux
// -----------------------------------------------------------------------------------------------
export type Arrow = 'up' | 'upright' | 'right' | 'downright' | 'down' | 'downleft' | 'left' | 'upleft';
/** Ordre de la grille de l'éditeur : ↖ ↑ ↗ ← → ↙ ↓ ↘. */
export const ARROWS: Arrow[] = ['upleft', 'up', 'upright', 'left', 'right', 'downleft', 'down', 'downright'];
export const ARROW_DEG: Record<Arrow, number> = { up: 0, upright: 45, right: 90, downright: 135, down: 180, downleft: 225, left: 270, upleft: 315 };
export const ARROW_GLYPH: Record<Arrow, string> = { up: '↑', upright: '↗', right: '→', downright: '↘', down: '↓', downleft: '↙', left: '←', upleft: '↖' };

export const COLORS = [
  { id: 'rouge', label: 'Rouge', hex: '#e5352b' }, { id: 'bleu', label: 'Bleu', hex: '#2f7bf5' },
  { id: 'vert', label: 'Vert', hex: '#22a63a' }, { id: 'jaune', label: 'Jaune', hex: '#f4c020' },
  { id: 'violet', label: 'Violet', hex: '#8b3fd6' }, { id: 'orange', label: 'Orange', hex: '#ec6a17' },
  { id: 'noir', label: 'Noir', hex: '#111111' }, { id: 'gris', label: 'Gris', hex: '#8a8f98' },
] as const;
export type ColorId = (typeof COLORS)[number]['id'];
export const colorHex = (id: string) => COLORS.find(c => c.id === id)?.hex ?? '#888888';

export const SHAPES = ['circle', 'triangle', 'square', 'diamond', 'pentagon', 'hexagon', 'octagon', 'star'] as const;
export type Shape = (typeof SHAPES)[number];
export const SHAPE_LABEL: Record<Shape, string> = {
  circle: 'Rond', triangle: 'Triangle', square: 'Carré', diamond: 'Losange', pentagon: 'Pentagone', hexagon: 'Hexagone', octagon: 'Octogone', star: 'Étoile',
};

export type Letters = { mode: 'range'; min: string; max: string } | { mode: 'select'; selected: string[] };
/** Une plage (de 1 à 10), ou des chiffres choisis un par un (1, 3, 5 : D-161). */
export type Numbers = { min: number; max: number } | { mode: 'select'; selected: number[] };
export const isNumberPick = (n: Numbers | null | undefined): n is { mode: 'select'; selected: number[] } => !!n && 'selected' in n;
export interface Stimuli {
  arrows?: Arrow[];
  colors?: ColorId[];
  numbers?: Numbers | null;
  letters?: Letters | null;
  shapes?: Shape[];
  /** Images du club (drill_images), lisibles du staff seulement. */
  images?: string[];
  /** Prénoms : l'effectif de l'équipe qui lance (« team »), ou une sélection de fiches. Jamais de photo. */
  players?: 'team' | string[] | null;
  /** Signes de systèmes (D-161) : une sélection, ou « ceux de l'équipe » (ceux qui ont un sens pour l'équipe qui lance). */
  signs?: SignId[] | 'team';
  /** Le nom du système sous le geste, après 1 s (sinon le geste seul). */
  sign_names?: boolean;
  /** Main gauche : le dessin se retourne (le côté du système). */
  sign_left?: boolean;
}

export type Cue =
  | { type: 'arrow'; value: Arrow }
  | { type: 'color'; value: string; hex: string }
  | { type: 'number'; value: number }
  | { type: 'letter'; value: string }
  | { type: 'shape'; value: Shape }
  | { type: 'image'; value: string }
  | { type: 'player'; value: string; name: string }
  /** `name` : le sens du signe pour l'équipe (sinon son nom), seulement si « le nom du système » est demandé. */
  | { type: 'sign'; value: SignId; left: boolean; name: string | null; label: string };

export interface PoolContext {
  /** Images lisibles par ce compte (ids) ; absent : toutes celles du drill. */
  images?: string[];
  /** L'effectif de l'équipe qui lance. */
  roster?: { id: string; name: string }[];
  /** Les signes de l'équipe qui lance : geste → son sens chez elle (« corne » → « Horns 4 »). */
  signNames?: Map<string, string>;
}

/** Développe les signaux choisis en une réserve de signaux, tirés un à la fois. */
export function expandPool(s: Stimuli | null | undefined, ctx: PoolContext = {}): Cue[] {
  const st = s ?? {}, pool: Cue[] = [];
  for (const a of st.arrows ?? []) if (a in ARROW_DEG) pool.push({ type: 'arrow', value: a });
  for (const c of st.colors ?? []) { const col = COLORS.find(x => x.id === c); if (col) pool.push({ type: 'color', value: col.id, hex: col.hex }); }
  if (isNumberPick(st.numbers)) {
    for (const n of new Set(st.numbers.selected)) if (Number.isInteger(n) && n >= 0 && n <= 999) pool.push({ type: 'number', value: n });
  } else if (st.numbers && Number.isFinite(st.numbers.min) && Number.isFinite(st.numbers.max)) {
    const lo = Math.max(0, Math.min(999, Math.min(st.numbers.min, st.numbers.max))), hi = Math.max(0, Math.min(999, Math.max(st.numbers.min, st.numbers.max)));
    for (let n = lo; n <= hi; n++) pool.push({ type: 'number', value: n });
  }
  if (st.letters) {
    if (st.letters.mode === 'select') {
      for (const x of new Set(st.letters.selected.map(l => String(l).toUpperCase().charAt(0)))) if (/[A-Z]/.test(x)) pool.push({ type: 'letter', value: x });
    } else {
      let lo = String(st.letters.min || 'A').toUpperCase().charCodeAt(0), hi = String(st.letters.max || 'A').toUpperCase().charCodeAt(0);
      if (lo > hi) [lo, hi] = [hi, lo];
      for (let c = Math.max(lo, 65); c <= Math.min(hi, 90); c++) pool.push({ type: 'letter', value: String.fromCharCode(c) });
    }
  }
  for (const sh of st.shapes ?? []) if ((SHAPES as readonly string[]).includes(sh)) pool.push({ type: 'shape', value: sh });
  const avail = ctx.images ? new Set(ctx.images) : null;
  for (const id of st.images ?? []) if (!avail || avail.has(id)) pool.push({ type: 'image', value: id });
  if (st.players && ctx.roster?.length) {
    const chosen = st.players === 'team' ? ctx.roster : ctx.roster.filter(p => (st.players as string[]).includes(p.id));
    // Un drill lancé par une autre équipe : sa sélection n'y est pas, l'effectif de l'équipe prend le relais.
    for (const p of chosen.length ? chosen : ctx.roster) pool.push({ type: 'player', value: p.id, name: p.name });
  }
  if (st.signs) {
    // « Ceux de l'équipe » : les gestes qui ont un sens pour elle ; aucun encore : six gestes courants.
    const team = [...(ctx.signNames?.keys() ?? [])].filter(isSignId);
    const ids = st.signs === 'team' ? (team.length ? team : DEFAULT_SIGNS) : st.signs.filter(isSignId);
    for (const id of new Set(ids)) pool.push({ type: 'sign', value: id, left: !!st.sign_left, label: signLabel(id),
      name: st.sign_names ? (ctx.signNames?.get(id) ?? signLabel(id)) : null });
  }
  return pool;
}

export const sameCue = (a: Cue | null | undefined, b: Cue | null | undefined) => !!a && !!b && a.type === b.type && String(a.value) === String(b.value);

/** Un signal au hasard, jamais deux fois le même de suite (sinon il semble rester affiché). */
export function pickCue(pool: Cue[], rnd: () => number = Math.random, prev: Cue | null = null): Cue | null {
  if (!pool.length) return null;
  const from = pool.length > 1 && prev ? pool.filter(c => !sameCue(c, prev)) : pool;
  return from[Math.min(from.length - 1, Math.floor(rnd() * from.length))] ?? null;
}

/** « 4 flèches · 2 couleurs · chiffres 1–10 · prénoms ». */
export function stimuliSummary(s: Stimuli | null | undefined): string {
  const st = s ?? {}, p: string[] = [];
  const n = (k: number, one: string, many: string) => `${k} ${k > 1 ? many : one}`;
  if (st.arrows?.length) p.push(n(st.arrows.length, 'flèche', 'flèches'));
  if (st.colors?.length) p.push(n(st.colors.length, 'couleur', 'couleurs'));
  if (isNumberPick(st.numbers)) p.push(`chiffres ${st.numbers.selected.join(', ')}`);
  else if (st.numbers) p.push(`chiffres ${Math.min(st.numbers.min, st.numbers.max)}–${Math.max(st.numbers.min, st.numbers.max)}`);
  if (st.letters) p.push(st.letters.mode === 'select' ? n(st.letters.selected.length, 'lettre', 'lettres') : `lettres ${st.letters.min}–${st.letters.max}`);
  if (st.shapes?.length) p.push(n(st.shapes.length, 'forme', 'formes'));
  if (st.images?.length) p.push(n(st.images.length, 'image', 'images'));
  if (st.players) p.push(st.players === 'team' ? 'prénoms' : n(st.players.length, 'prénom', 'prénoms'));
  if (st.signs) p.push(st.signs === 'team' ? 'signes de l’équipe' : n(st.signs.length, 'signe', 'signes'));
  return p.join(' · ') || 'aucun signal';
}

// -----------------------------------------------------------------------------------------------
// Réaction : le rythme
// -----------------------------------------------------------------------------------------------
export type Rhythm = 'fixe' | 'accelere' | 'hasard';
export interface ReactionConfig {
  stimuli: Stimuli;
  /** Durée d'un signal (au début, s'il accélère). */
  length_ms: number;
  rhythm: Rhythm;
  /** Accélère : durée au dernier signal. */
  end_ms?: number;
  /** Au hasard : entre ces deux durées. */
  min_ms?: number;
  max_ms?: number;
  /** Le noir entre deux signaux. */
  delay_ms: number;
  duration: { mode: 'signals' | 'minutes'; value: number };
  /** Décompte pendant le noir (noir d'une seconde au moins). */
  anticipation: boolean;
  beep: boolean;
  /** D-161 : « Au hasard » (absent : les drills d'avant) ou « En séquence » (la frise `sequence`). */
  mode?: 'hasard' | 'sequence';
  sequence?: Sequence;
}
export const LEN_MIN = 500, LEN_MAX = 15000, DELAY_MAX = 5000;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Durée du signal n° i (0 = le premier) sur `total`. */
export function cueLength(c: ReactionConfig, i: number, total: number, rnd: () => number = Math.random): number {
  let ms = c.length_ms;
  if (c.rhythm === 'hasard') {
    const lo = Math.min(c.min_ms ?? c.length_ms, c.max_ms ?? c.length_ms), hi = Math.max(c.min_ms ?? c.length_ms, c.max_ms ?? c.length_ms);
    ms = lo + rnd() * (hi - lo);
  } else if (c.rhythm === 'accelere' && total > 1) {
    ms = c.length_ms + ((c.end_ms ?? c.length_ms) - c.length_ms) * clamp(i / (total - 1), 0, 1);
  }
  return clamp(Math.round(ms), 200, LEN_MAX);
}

/** Durée moyenne d'un signal. */
export function averageLength(c: ReactionConfig): number {
  if (c.rhythm === 'hasard') return ((c.min_ms ?? c.length_ms) + (c.max_ms ?? c.length_ms)) / 2;
  if (c.rhythm === 'accelere') return (c.length_ms + (c.end_ms ?? c.length_ms)) / 2;
  return c.length_ms;
}

/** Nombre de signaux (estimé en minutes : signal moyen + noir). */
export function totalSignals(c: ReactionConfig): number {
  if (c.duration.mode === 'minutes') return Math.max(1, Math.round((Math.max(1, c.duration.value) * 60_000) / Math.max(200, averageLength(c) + c.delay_ms)));
  return Math.max(1, Math.round(c.duration.value));
}

/** Durée du drill (le dernier signal n'a pas de noir après lui). */
export function reactionDurationMs(c: ReactionConfig): number {
  if (c.duration.mode === 'minutes') return Math.max(1, c.duration.value) * 60_000;
  const n = totalSignals(c);
  return Math.round(averageLength(c) * n + c.delay_ms * (n - 1));
}

/** La frise de l'éditeur : signal, noir, signal… (« … » ensuite), pris au début, au tiers, aux deux tiers. */
export function rhythmPreview(c: ReactionConfig, signals = 3): { kind: 'signal' | 'noir'; ms: number }[] {
  const total = totalSignals(c), out: { kind: 'signal' | 'noir'; ms: number }[] = [];
  for (let j = 0; j < Math.min(signals, total); j++) {
    // Au hasard : la moyenne (l'aperçu ne tire pas au sort).
    const i = Math.round((j * (total - 1)) / signals);
    out.push({ kind: 'signal', ms: c.rhythm === 'hasard' ? averageLength(c) : cueLength(c, i, total) });
    if (c.delay_ms > 0) out.push({ kind: 'noir', ms: c.delay_ms });
  }
  return out;
}

// -----------------------------------------------------------------------------------------------
// Formats
// -----------------------------------------------------------------------------------------------
/** « 2 s », « 0,5 s », « 1,7 s ». */
export function fmtSec(ms: number): string {
  const s = Math.round(ms / 100) / 10;
  return `${String(s).replace('.', ',')} s`;
}
/** « 0:09 », « 14:20 » (secondes entamées, comme un décompte). */
export function fmtClock(ms: number): string {
  const t = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
}
/** « 40 secondes », « 3 min », « 5 min 30 », « 1 h 10 » (arrondi à 5 s, puis à 30 s). */
export function fmtAbout(ms: number): string {
  const s = Math.max(0, ms / 1000);
  if (s < 60) return `${Math.max(5, Math.round(s / 5) * 5)} secondes`;
  const half = Math.round(s / 30) * 30, m = Math.floor(half / 60), r = half % 60;
  if (m >= 60) return `${Math.floor(m / 60)} h${m % 60 ? ` ${String(m % 60).padStart(2, '0')}` : ''}`;
  return `${m} min${r ? ' 30' : ''}`;
}
/** « 15 s », « 1 min 30 », « 5 min ». */
export function fmtShort(sec: number): string {
  const s = Math.round(sec);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60), r = s % 60;
  return r ? `${m} min ${r}` : `${m} min`;
}

/** « aujourd'hui », « hier », « lundi », « il y a 6 j », « le 3 sept. ». */
export function agoLabel(iso: string, now: Date): string {
  const d = new Date(iso);
  const day = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
  const n = Math.round((day(now) - day(d)) / 86_400_000);
  if (n <= 0) return 'aujourd’hui';
  if (n === 1) return 'hier';
  if (n <= 4) return d.toLocaleDateString('fr-FR', { weekday: 'long' });
  if (n <= 60) return `il y a ${n} j`;
  return `le ${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
}

/** Recherche tolérante : sans accents ni majuscules, lettres dans l'ordre (« suic20 » → « Suicide 20 s »). */
export const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
export function nameMatches(name: string, q: string): boolean {
  const t = fold(q).replace(/\s+/g, '');
  if (!t) return true;
  const n = fold(name);
  if (n.includes(t)) return true;
  let i = 0;
  for (let k = 0; k < n.length && i < t.length; k++) if (n[k] === t[i]) i++;
  return i === t.length;
}

// -----------------------------------------------------------------------------------------------
// Circuit : une suite d'étapes
// -----------------------------------------------------------------------------------------------
export type StageType = 'counter' | 'countdown' | 'signals';
export const STAGE_LABEL: Record<StageType, string> = { counter: 'Compteur', countdown: 'Compte à rebours', signals: 'Signaux' };
export const STAGE_COLOR: Record<StageType, string> = { counter: '#7C3AED', countdown: '#D97706', signals: '#2563EB' };
export interface Stage {
  id: string;
  type: StageType;
  label: string;
  /** Compteur : de `start` à `cap`, `step` toutes les `every_s` secondes (descend si cap < start). */
  start?: number;
  cap?: number;
  step?: number;
  every_s?: number;
  /** Compte à rebours et signaux : la durée. */
  seconds?: number;
  beep_start?: boolean;
  beep_last3?: boolean;
  /** Signaux : repris d'un drill de réaction (copie de ses signaux), ou choisis ici. */
  from?: { id: string; title: string } | null;
  stimuli?: Stimuli;
  length_ms?: number;
  delay_ms?: number;
  /** Signaux repris d'un drill de réaction « en séquence » (D-161) : la frise tourne pendant l'étape. */
  sequence?: Sequence | null;
  /** Passage : tout seul (au plafond, au temps), ou au toucher « Suivant ». */
  advance: 'auto' | 'tap';
  /** Repos après l'étape (secondes). */
  rest_s: number;
}
export interface CircuitConfig { stages: Stage[]; auto: boolean; voice: boolean }

const down = (s: Stage) => (s.cap ?? 0) < (s.start ?? 0);
/** Valeur du compteur après `ticks` incréments (bornée au plafond). */
export function counterValue(s: Stage, ticks: number): number {
  const start = s.start ?? 0, cap = s.cap ?? start, step = Math.abs(s.step ?? 1) || 1;
  const v = start + (down(s) ? -1 : 1) * step * Math.max(0, ticks);
  return down(s) ? Math.max(cap, v) : Math.min(cap, v);
}
/** Incréments écoulés au bout de `elapsedMs`. */
export const counterTicks = (s: Stage, elapsedMs: number) => Math.floor(Math.max(0, elapsedMs) / (Math.max(1, s.every_s ?? 5) * 1000));
/** Incréments pour atteindre le plafond. */
export const ticksToCap = (s: Stage) => Math.ceil(Math.abs((s.cap ?? 0) - (s.start ?? 0)) / (Math.abs(s.step ?? 1) || 1));

/** L'étape est-elle finie (passage tout seul) ? Jamais en mode manuel ni « au toucher ». */
export function stageDone(s: Stage, elapsedMs: number, auto: boolean): boolean {
  if (!auto || s.advance === 'tap') return false;
  if (s.type === 'counter') return counterValue(s, counterTicks(s, elapsedMs)) === (s.cap ?? 0);
  return elapsedMs >= Math.max(1, s.seconds ?? 30) * 1000;
}
/** Durée prévue d'une étape. */
export function stageDurationMs(s: Stage): number {
  if (s.type === 'counter') return ticksToCap(s) * Math.max(1, s.every_s ?? 5) * 1000;
  return Math.max(1, s.seconds ?? 30) * 1000;
}
/** Durée du circuit : étapes et repos (pas de repos après la dernière). */
export function circuitDurationMs(c: CircuitConfig): number {
  return c.stages.reduce((n, s, i) => n + stageDurationMs(s) + (i < c.stages.length - 1 ? Math.max(0, s.rest_s) * 1000 : 0), 0);
}
/** « de 5 à 30 · +1 toutes les 8 s · passe au plafond ». */
export function stageSummary(s: Stage): string {
  if (s.type === 'counter') {
    const step = Math.abs(s.step ?? 1) || 1;
    return `de ${s.start ?? 0} à ${s.cap ?? 0} · ${down(s) ? '−' : '+'}${step} toutes les ${s.every_s ?? 5} s${s.advance === 'auto' ? ' · passe au plafond' : ''}`;
  }
  if (s.type === 'countdown') {
    const b = [s.beep_start ? 'bip au départ' : '', s.beep_last3 ? '3-2-1 à la fin' : ''].filter(Boolean);
    return `${fmtShort(s.seconds ?? 30)}${b.length ? ` · ${b.join(', ')}` : ''}`;
  }
  return `${fmtShort(s.seconds ?? 30)} · ${s.from ? `reprend « ${s.from.title} »${s.sequence ? ' (en séquence)' : ''}` : stimuliSummary(s.stimuli)}`;
}
/** « Repos 30 s · « Ensuite : Gainage face » », « Sans repos ». */
export function restLine(s: Stage, next: Stage | undefined): string {
  if (!s.rest_s) return 'Sans repos';
  return `Repos ${fmtShort(s.rest_s)}${next ? ` · « Ensuite : ${next.label || STAGE_LABEL[next.type]} »` : ''}`;
}
/** Ce qui attend après un repos : « 20 répétitions », « 45 s », « 4 flèches ». */
export function stagePreview(s: Stage): string {
  if (s.type === 'counter') return `jusqu’à ${s.cap ?? 0}`;
  if (s.type === 'countdown') return fmtShort(s.seconds ?? 30);
  return s.from ? s.from.title : stimuliSummary(s.stimuli);
}

// -----------------------------------------------------------------------------------------------
// Fractionné : échauffement, cycles effort / récup, retour au calme
// -----------------------------------------------------------------------------------------------
export type Progression = 'regulier' | 'progressif' | 'pyramide';
export interface IntervalConfig {
  warmup_s: number;
  work_s: number;
  rest_s: number;
  cycles: number;
  cooldown_s: number;
  progression: Progression;
  /** Progressif : secondes d'effort en plus à chaque cycle. */
  step_s?: number;
  /** Pyramide : l'effort au sommet (au milieu de la séance). */
  peak_s?: number;
  /** Les consignes, sous le temps : « sprint aller-retour, ligne de fond », « marche ». */
  work_label?: string;
  rest_label?: string;
  beeps: boolean;
  voice: boolean;
  /** Le modèle de départ (tabata, sprint…). */
  model?: string;
}
export const INTERVAL_MODELS: { key: string; name: string; cfg: Pick<IntervalConfig, 'warmup_s' | 'work_s' | 'rest_s' | 'cycles' | 'cooldown_s'> }[] = [
  { key: 'tabata', name: 'Tabata', cfg: { warmup_s: 0, work_s: 20, rest_s: 10, cycles: 8, cooldown_s: 0 } },
  { key: 'hiit-court', name: 'HIIT court', cfg: { warmup_s: 180, work_s: 30, rest_s: 30, cycles: 10, cooldown_s: 120 } },
  { key: 'hiit-long', name: 'HIIT long', cfg: { warmup_s: 300, work_s: 60, rest_s: 60, cycles: 8, cooldown_s: 180 } },
  { key: 'sprint', name: 'Sprint basket', cfg: { warmup_s: 300, work_s: 15, rest_s: 45, cycles: 12, cooldown_s: 180 } },
  { key: 'long', name: 'Long', cfg: { warmup_s: 300, work_s: 90, rest_s: 30, cycles: 10, cooldown_s: 300 } },
];

export type PhaseType = 'warmup' | 'work' | 'rest' | 'cooldown';
export const PHASE_LABEL: Record<PhaseType, string> = { warmup: 'Échauffement', work: 'Effort', rest: 'Récup', cooldown: 'Retour au calme' };
export interface Phase { type: PhaseType; ms: number; start: number; end: number; cycle?: number; of?: number; sub?: string }

const pyramid = (start: number, peak: number, i: number, n: number) => {
  if (n <= 1) return peak;
  const mid = (n - 1) / 2;
  return start + (peak - start) * (1 - Math.abs(i - mid) / mid);
};
/** Les cycles, selon la progression. */
export function cyclesOf(c: IntervalConfig): { work_s: number; rest_s: number }[] {
  const n = Math.max(1, Math.min(99, Math.round(c.cycles)));
  return Array.from({ length: n }, (_, i) => ({
    work_s: Math.max(1, Math.round(c.progression === 'progressif' ? c.work_s + i * (c.step_s ?? 5)
      : c.progression === 'pyramide' ? pyramid(c.work_s, c.peak_s ?? c.work_s * 2, i, n) : c.work_s)),
    rest_s: Math.max(0, Math.round(c.rest_s)),
  }));
}
/** Les phases, avec leurs bornes depuis le départ : la phase en cours se retrouve à tout instant (écran éteint compris). */
export function intervalTimeline(c: IntervalConfig): Phase[] {
  const out: Phase[] = [];
  let t = 0;
  const push = (p: Omit<Phase, 'start' | 'end'>) => { if (p.ms > 0) { out.push({ ...p, start: t, end: t + p.ms }); t += p.ms; } };
  push({ type: 'warmup', ms: Math.max(0, c.warmup_s) * 1000 });
  const cy = cyclesOf(c);
  cy.forEach((x, i) => {
    push({ type: 'work', ms: x.work_s * 1000, cycle: i + 1, of: cy.length, sub: c.work_label });
    push({ type: 'rest', ms: x.rest_s * 1000, cycle: i + 1, of: cy.length, sub: c.rest_label });
  });
  push({ type: 'cooldown', ms: Math.max(0, c.cooldown_s) * 1000 });
  return out;
}
export const intervalTotalMs = (c: IntervalConfig) => intervalTimeline(c).reduce((n, p) => n + p.ms, 0);
/** La phase à `elapsedMs` du départ ; null : c'est fini. */
export function phaseAt(tl: Phase[], elapsedMs: number): { index: number; phase: Phase; elapsed: number; remaining: number } | null {
  const e = Math.max(0, elapsedMs);
  for (let i = 0; i < tl.length; i++) if (e < tl[i]!.end) return { index: i, phase: tl[i]!, elapsed: e - tl[i]!.start, remaining: tl[i]!.end - e };
  return null;
}
/** « Échauf. 5 min », « 12 × 15 s / 45 s », « Calme 3 min ». */
export function intervalPills(c: IntervalConfig): { key: 'warmup' | 'cycles' | 'cooldown'; text: string }[] {
  const cy = cyclesOf(c);
  const same = cy.every(x => x.work_s === cy[0]!.work_s);
  return [
    { key: 'warmup', text: c.warmup_s ? `Échauf. ${fmtShort(c.warmup_s)}` : '+ Échauffement' },
    { key: 'cycles', text: `${cy.length} × ${same ? fmtShort(c.work_s) : `${fmtShort(cy[0]!.work_s)}…`} / ${fmtShort(c.rest_s)}` },
    { key: 'cooldown', text: c.cooldown_s ? `Calme ${fmtShort(c.cooldown_s)}` : '+ Retour au calme' },
  ];
}
/** « Échauf. 5 min · 12 × (15 s / 45 s) · retour 3 min · 20 min ». */
export function intervalSummary(c: IntervalConfig): string {
  const cy = cyclesOf(c);
  return [c.warmup_s ? `Échauf. ${fmtShort(c.warmup_s)}` : '', `${cy.length} × (${fmtShort(c.work_s)} / ${fmtShort(c.rest_s)})`,
    c.cooldown_s ? `retour ${fmtShort(c.cooldown_s)}` : '', fmtShort(intervalTotalMs(c) / 1000)].filter(Boolean).join(' · ');
}

// -----------------------------------------------------------------------------------------------
// Configurations : par défaut, et relues sans confiance (jsonb)
// -----------------------------------------------------------------------------------------------
const num = (v: unknown, d: number, lo: number, hi: number) => (typeof v === 'number' && Number.isFinite(v) ? clamp(v, lo, hi) : d);
const bool = (v: unknown, d: boolean) => (typeof v === 'boolean' ? v : d);
const str = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : undefined);

export function defaultReaction(): ReactionConfig {
  return { stimuli: { arrows: ['left', 'up', 'right', 'down'] }, length_ms: 2000, rhythm: 'fixe', end_ms: 800, min_ms: 1000, max_ms: 3000,
    delay_ms: 500, duration: { mode: 'signals', value: 20 }, anticipation: false, beep: true };
}
export function defaultInterval(model = 'sprint'): IntervalConfig {
  const m = INTERVAL_MODELS.find(x => x.key === model) ?? INTERVAL_MODELS[3]!;
  return { ...m.cfg, progression: 'regulier', step_s: 5, peak_s: m.cfg.work_s * 2, beeps: true, voice: false, model: m.key };
}
export function defaultCircuit(): CircuitConfig { return { stages: [], auto: true, voice: true }; }

export function readStimuli(v: unknown): Stimuli {
  const s = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const list = <T extends string>(x: unknown, ok: readonly string[]) => (Array.isArray(x) ? (x.filter(y => typeof y === 'string' && ok.includes(y)) as T[]) : undefined);
  const out: Stimuli = {};
  const arrows = list<Arrow>(s.arrows, ARROWS); if (arrows?.length) out.arrows = arrows;
  const colors = list<ColorId>(s.colors, COLORS.map(c => c.id)); if (colors?.length) out.colors = colors;
  const shapes = list<Shape>(s.shapes, SHAPES); if (shapes?.length) out.shapes = shapes;
  const n = s.numbers as { min?: unknown; max?: unknown } | null | undefined;
  const pick = s.numbers as { mode?: unknown; selected?: unknown } | null | undefined;
  if (pick && pick.mode === 'select' && Array.isArray(pick.selected)) {
    const sel = [...new Set(pick.selected.filter((x): x is number => typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= 999))].slice(0, 20);
    if (sel.length) out.numbers = { mode: 'select', selected: sel };
  } else if (n && typeof n.min === 'number' && typeof n.max === 'number') out.numbers = { min: clamp(Math.round(n.min), 0, 999), max: clamp(Math.round(n.max), 0, 999) };
  const l = s.letters as Record<string, unknown> | null | undefined;
  if (l && l.mode === 'select' && Array.isArray(l.selected)) out.letters = { mode: 'select', selected: l.selected.filter(x => typeof x === 'string' && /^[A-Z]$/.test(x)) as string[] };
  else if (l && typeof l.min === 'string' && typeof l.max === 'string') out.letters = { mode: 'range', min: l.min.toUpperCase().slice(0, 1) || 'A', max: l.max.toUpperCase().slice(0, 1) || 'H' };
  if (Array.isArray(s.images)) { const ids = s.images.filter(x => typeof x === 'string') as string[]; if (ids.length) out.images = ids.slice(0, 40); }
  if (s.players === 'team') out.players = 'team';
  else if (Array.isArray(s.players)) out.players = (s.players.filter(x => typeof x === 'string') as string[]).slice(0, 60);
  if (s.signs === 'team') out.signs = 'team';
  else if (Array.isArray(s.signs)) { const ids = [...new Set(s.signs.filter(isSignId))]; if (ids.length) out.signs = ids; }
  if (out.signs) { if (s.sign_names === true) out.sign_names = true; if (s.sign_left === true) out.sign_left = true; }
  return out;
}
export function readReaction(v: unknown): ReactionConfig {
  const d = defaultReaction(), c = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const dur = (c.duration ?? {}) as Record<string, unknown>;
  const mode = dur.mode === 'minutes' ? 'minutes' : 'signals';
  return {
    stimuli: c.stimuli ? readStimuli(c.stimuli) : d.stimuli,
    length_ms: num(c.length_ms, d.length_ms, LEN_MIN, LEN_MAX),
    rhythm: c.rhythm === 'accelere' || c.rhythm === 'hasard' ? c.rhythm : 'fixe',
    end_ms: num(c.end_ms, d.end_ms!, LEN_MIN, LEN_MAX), min_ms: num(c.min_ms, d.min_ms!, LEN_MIN, LEN_MAX), max_ms: num(c.max_ms, d.max_ms!, LEN_MIN, LEN_MAX),
    delay_ms: num(c.delay_ms, d.delay_ms, 0, DELAY_MAX),
    duration: { mode, value: num(dur.value, mode === 'minutes' ? 3 : 20, 1, mode === 'minutes' ? 60 : 500) },
    anticipation: bool(c.anticipation, false), beep: bool(c.beep, true),
    // D-161 : le mode et la frise ; un drill d'avant n'a ni l'un ni l'autre (« Au hasard »).
    ...(c.mode === 'sequence' ? { mode: 'sequence' as const } : c.mode === 'hasard' ? { mode: 'hasard' as const } : {}),
    ...(c.mode === 'sequence' || c.sequence ? { sequence: readSequence(c.sequence) } : {}),
  };
}
export function readInterval(v: unknown): IntervalConfig {
  const d = defaultInterval(), c = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  return {
    warmup_s: num(c.warmup_s, d.warmup_s, 0, 3600), work_s: num(c.work_s, d.work_s, 1, 3600), rest_s: num(c.rest_s, d.rest_s, 0, 3600),
    cycles: num(c.cycles, d.cycles, 1, 99), cooldown_s: num(c.cooldown_s, d.cooldown_s, 0, 3600),
    progression: c.progression === 'progressif' || c.progression === 'pyramide' ? c.progression : 'regulier',
    step_s: num(c.step_s, 5, -60, 120), peak_s: num(c.peak_s, d.work_s * 2, 1, 3600),
    work_label: str(c.work_label, 80), rest_label: str(c.rest_label, 80),
    beeps: bool(c.beeps, true), voice: bool(c.voice, false), model: str(c.model, 20),
  };
}
export function readCircuit(v: unknown): CircuitConfig {
  const c = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const stages = (Array.isArray(c.stages) ? c.stages : []).slice(0, 30).map((x, i): Stage | null => {
    const s = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
    const type: StageType = s.type === 'countdown' || s.type === 'signals' ? s.type : s.type === 'counter' ? 'counter' : 'countdown';
    const from = s.from as { id?: unknown; title?: unknown } | null | undefined;
    return {
      id: typeof s.id === 'string' ? s.id.slice(0, 12) : `s${i}`, type, label: str(s.label, 60) ?? '',
      start: num(s.start, 5, 0, 999), cap: num(s.cap, 30, 0, 999), step: num(s.step, 1, 1, 100), every_s: num(s.every_s, 5, 1, 600),
      seconds: num(s.seconds, 30, 1, 3600), beep_start: bool(s.beep_start, true), beep_last3: bool(s.beep_last3, true),
      from: from && typeof from.id === 'string' && typeof from.title === 'string' ? { id: from.id, title: from.title.slice(0, 80) } : null,
      stimuli: readStimuli(s.stimuli), length_ms: num(s.length_ms, 2000, LEN_MIN, LEN_MAX), delay_ms: num(s.delay_ms, 500, 0, DELAY_MAX),
      advance: s.advance === 'tap' ? 'tap' : 'auto', rest_s: num(s.rest_s, 0, 0, 600),
      ...(type === 'signals' && s.sequence ? { sequence: readSequence(s.sequence) } : {}),
    };
  }).filter((s): s is Stage => !!s);
  return { stages, auto: bool(c.auto, true), voice: bool(c.voice, true) };
}

/** Le résumé d'une carte : « 4 flèches · 2 s puis 0,5 s de noir · 20 signaux · bip ». */
export function drillSummary(kind: DrillKind, config: unknown): string {
  if (kind === 'fractionne') return intervalSummary(readInterval(config));
  if (kind === 'circuit') {
    const c = readCircuit(config);
    return `${c.stages.length} étape${c.stages.length > 1 ? 's' : ''} · ≈ ${fmtAbout(circuitDurationMs(c))}${c.voice ? ' · voix' : ''}`;
  }
  const c = readReaction(config);
  if (c.mode === 'sequence' && c.sequence) return sequenceSummary(c.sequence);
  const rhythm = c.rhythm === 'hasard' ? `${fmtSec(Math.min(c.min_ms!, c.max_ms!)).replace(' s', '')} à ${fmtSec(Math.max(c.min_ms!, c.max_ms!))} au hasard`
    : c.rhythm === 'accelere' ? `${fmtSec(c.length_ms)} → ${fmtSec(c.end_ms!)}` : fmtSec(c.length_ms);
  return [stimuliSummary(c.stimuli), `${rhythm}${c.delay_ms ? ` puis ${fmtSec(c.delay_ms)} de noir` : ''}`,
    c.duration.mode === 'minutes' ? `${c.duration.value} min` : `${c.duration.value} signaux`, c.anticipation ? 'décompte' : '', c.beep ? 'bip' : ''].filter(Boolean).join(' · ');
}

/** Durée d'un drill (≈), pour « Lecture défensive : flèches · 40 s ». */
export function drillDurationMs(kind: DrillKind, config: unknown): number {
  if (kind === 'reaction') { const r = readReaction(config); if (r.mode === 'sequence' && r.sequence) return sequenceDurationMs(r.sequence); }
  return kind === 'fractionne' ? intervalTotalMs(readInterval(config)) : kind === 'circuit' ? circuitDurationMs(readCircuit(config)) : reactionDurationMs(readReaction(config));
}

// -----------------------------------------------------------------------------------------------
// Réaction « en séquence » (D-161, choix 7 ; maquettes d1 et d2 de D-162) : une frise de briques Son, Attente,
// Signal (douze au plus), répétée N tours ou X minutes ; « Mélanger » change l'ordre des signaux à chaque tour
// (attentes et sons restent en place). Le moteur lit le plan à l'instant voulu : le temps est calé sur l'horloge,
// comme le fractionné (juste après une mise en veille).
// -----------------------------------------------------------------------------------------------
export type SeqSound = 'bip' | 'bip-long' | 'sifflet' | 'buzzer' | 'cloche' | 'go';
export const SEQ_SOUNDS: { id: SeqSound; name: string; short: string }[] = [
  { id: 'bip', name: 'Bip court', short: 'bip' }, { id: 'bip-long', name: 'Bip long', short: 'bip long' },
  { id: 'sifflet', name: 'Sifflet', short: 'sifflet' }, { id: 'buzzer', name: 'Buzzer de salle', short: 'buzzer' },
  { id: 'cloche', name: 'Cloche', short: 'cloche' }, { id: 'go', name: 'La voix « Go »', short: 'go' },
];
export type SeqStep =
  | { kind: 'son'; sound: SeqSound }
  /** Au hasard entre `ms` et `max_ms` quand `max_ms` est là : impossible d'anticiper. */
  | { kind: 'attente'; ms: number; max_ms?: number }
  | { kind: 'signal'; stimuli: Stimuli; show_ms: number; beep: boolean };
export type SeqKind = SeqStep['kind'];
export interface Sequence {
  steps: SeqStep[];
  repeat: { mode: 'tours' | 'minutes'; value: number };
  shuffle: boolean;
  /** 3, 2, 1 avant la première brique. */
  countdown: boolean;
  /** 3, 2, 1 avant chaque tour (Idries : « ajoute le décompte de 3 secondes à chaque tour ») ; il remplace alors le décompte du départ. */
  countdown_each: boolean;
}
export const SEQ_MAX = 12, SOUND_MS = 500, WAIT_MIN = 200, WAIT_MAX = 10_000, SHOW_MIN = 300, SHOW_MAX = 10_000, COUNTDOWN_MS = 3000;
export const SEQ_KIND_LABEL: Record<SeqKind, string> = { son: 'Son', attente: 'Attente', signal: 'Signal' };

/** Durée moyenne d'une brique (une attente au hasard : le milieu). */
export function stepAvgMs(s: SeqStep): number {
  if (s.kind === 'son') return SOUND_MS;
  if (s.kind === 'signal') return s.show_ms;
  return s.max_ms !== undefined ? (s.ms + s.max_ms) / 2 : s.ms;
}
/** Durée tirée d'une brique. */
export function stepMs(s: SeqStep, rnd: () => number = Math.random): number {
  if (s.kind === 'attente' && s.max_ms !== undefined) {
    const lo = Math.min(s.ms, s.max_ms), hi = Math.max(s.ms, s.max_ms);
    return Math.round(lo + rnd() * (hi - lo));
  }
  return Math.round(stepAvgMs(s));
}
/** Un tour (≈), ses briques seules. */
export const tourMs = (q: Sequence) => q.steps.reduce((n, s) => n + stepAvgMs(s), 0);
/** Un tour (≈) avec son décompte quand il y en a un à chaque tour. */
export const tourCycleMs = (q: Sequence) => tourMs(q) + (q.countdown_each && q.steps.length ? COUNTDOWN_MS : 0);
/** Nombre de tours (≈ en minutes). */
export function seqTours(q: Sequence): number {
  if (q.repeat.mode === 'tours') return Math.max(1, Math.round(q.repeat.value));
  const t = tourCycleMs(q);
  return t > 0 ? Math.max(1, Math.ceil((q.repeat.value * 60_000) / t)) : 1;
}
/** Durée du drill (sans le décompte). */
export function sequenceDurationMs(q: Sequence): number {
  return q.repeat.mode === 'minutes' ? Math.max(1, q.repeat.value) * 60_000 : Math.round(seqTours(q) * tourCycleMs(q));
}
/** « En séquence · 5 briques · ≈ 5,5 s le tour · 20 tours (1 min 50) ». */
export function sequenceSummary(q: Sequence): string {
  const n = q.steps.length;
  return ['En séquence', `${n} brique${n > 1 ? 's' : ''}`, `≈ ${fmtSec(tourMs(q))} le tour`,
    q.repeat.mode === 'minutes' ? `${q.repeat.value} min` : `${seqTours(q)} tours (${fmtShort(sequenceDurationMs(q) / 1000)})`, q.shuffle ? 'mélangé' : '', q.countdown_each ? 'décompte à chaque tour' : ''].filter(Boolean).join(' · ');
}

/** L'ordre des briques d'un tour : « Mélanger » permute les signaux entre leurs places ; sons et attentes ne bougent pas. */
export function tourOrder(q: Sequence, rnd: () => number = Math.random): number[] {
  const order = q.steps.map((_, i) => i);
  if (!q.shuffle) return order;
  const slots = order.filter(i => q.steps[i]!.kind === 'signal');
  const picks = [...slots];
  for (let i = picks.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [picks[i], picks[j]] = [picks[j]!, picks[i]!]; }
  slots.forEach((slot, k) => { order[slot] = picks[k]!; });
  return order;
}

/** `decompte` : le 3, 2, 1 avant un tour (slot et step à -1). */
export interface SeqEvent { tour: number; slot: number; step: number; kind: SeqKind | 'decompte'; start: number; end: number }
export interface SeqPlan {
  events: SeqEvent[];
  /** La brique en cours à `e` ms du départ ; null : c'est fini. */
  at(e: number): { index: number; ev: SeqEvent } | null;
  /** Les briques du tour `n` (déjà construit). */
  tour(n: number): SeqEvent[];
}
/** Le plan d'une séquence, construit tour par tour à la demande : après une mise en veille, `at` retombe sur la bonne
 *  brique. Les attentes au hasard et l'ordre mélangé sont tirés quand le tour est construit. */
export function seqPlan(q: Sequence, rnd: () => number = Math.random): SeqPlan {
  const events: SeqEvent[] = [];
  const limit = q.repeat.mode === 'minutes' ? Math.max(1, q.repeat.value) * 60_000 : Infinity;
  const tours = q.repeat.mode === 'tours' ? Math.max(1, Math.round(q.repeat.value)) : Infinity;
  let built = 0, t = 0, last = 0;
  const grow = (): boolean => {
    if (!q.steps.length || built >= tours || t >= limit) return false;
    const order = tourOrder(q, rnd);
    built++;
    if (q.countdown_each) { events.push({ tour: built, slot: -1, step: -1, kind: 'decompte', start: t, end: Math.min(t + COUNTDOWN_MS, limit) }); t += COUNTDOWN_MS; }
    for (let k = 0; k < order.length && t < limit; k++) {
      const i = order[k]!, ms = Math.max(1, stepMs(q.steps[i]!, rnd));
      events.push({ tour: built, slot: k, step: i, kind: q.steps[i]!.kind, start: t, end: Math.min(t + ms, limit) });
      t += ms;
    }
    return true;
  };
  return {
    events,
    at(e) {
      while (!events.length || events[events.length - 1]!.end <= e) if (!grow()) break;
      if (last >= events.length || events[last]!.start > e) last = 0;
      for (let i = last; i < events.length; i++) if (e < events[i]!.end) { last = i; return e >= events[i]!.start ? { index: i, ev: events[i]! } : null; }
      return null;
    },
    tour: (n) => events.filter(x => x.tour === n),
  };
}

/** Le titre d'une brique Signal : « Un chiffre parmi 1, 3, 5 », « Une flèche, au hasard », « Un signe de l'équipe ». */
export function signalTitle(st: Stimuli): string {
  const fams = (['arrows', 'colors', 'numbers', 'letters', 'shapes', 'images', 'players', 'signs'] as const).filter(k => {
    const v = st[k]; return Array.isArray(v) ? v.length > 0 : !!v;
  });
  if (fams.length !== 1) return fams.length ? `Un signal au hasard : ${stimuliSummary(st)}` : 'Aucun signal choisi';
  const col = (id: string) => COLORS.find(c => c.id === id)?.label.toLowerCase() ?? id;
  switch (fams[0]) {
    case 'arrows': return st.arrows!.length === 1 ? `La flèche ${ARROW_GLYPH[st.arrows![0]!]}` : 'Une flèche, au hasard';
    case 'colors': return st.colors!.length === 1 ? `Le ${col(st.colors![0]!)}` : st.colors!.length <= 4 ? `Une couleur parmi ${st.colors!.map(col).join(', ')}` : `Une couleur parmi ${st.colors!.length}`;
    case 'numbers': return isNumberPick(st.numbers) ? (st.numbers.selected.length === 1 ? `Le chiffre ${st.numbers.selected[0]}` : `Un chiffre parmi ${st.numbers.selected.join(', ')}`)
      : `Un chiffre de ${Math.min(st.numbers!.min, st.numbers!.max)} à ${Math.max(st.numbers!.min, st.numbers!.max)}`;
    case 'letters': return st.letters!.mode === 'select' ? `Une lettre parmi ${st.letters!.selected.join(', ')}` : `Une lettre de ${st.letters!.min} à ${st.letters!.max}`;
    case 'shapes': return `Une forme parmi ${st.shapes!.length}`;
    case 'images': return st.images!.length === 1 ? 'Une image du club' : `Une image du club parmi ${st.images!.length}`;
    case 'players': return 'Un prénom de l’équipe';
    default: return st.signs === 'team' ? 'Un signe de l’équipe' : st.signs!.length === 1 ? `Le signe « ${signLabel(st.signs![0]!)} »` : `Un signe parmi ${st.signs!.length}`;
  }
}
/** Le sous-titre d'une brique : « reste 1 s · sans bip », « écran noir ». */
export function stepLine(s: SeqStep): string {
  if (s.kind === 'son') return 'le son seul, écran noir';
  if (s.kind === 'attente') return s.max_ms !== undefined ? 'écran noir · impossible d’anticiper' : 'écran noir · ou au hasard entre deux durées';
  return `reste ${fmtSec(s.show_ms)} · ${s.beep ? 'avec bip' : 'sans bip'}${s.stimuli.signs && s.stimuli.sign_names ? ' · le nom après 1 s' : ''}`;
}
/** « 2 secondes », « 1 à 3 secondes », « 0,5 seconde ». */
export function waitTitle(s: { ms: number; max_ms?: number }): string {
  const sec = (ms: number) => String(Math.round(ms / 100) / 10).replace('.', ',');
  if (s.max_ms !== undefined) return `${sec(Math.min(s.ms, s.max_ms))} à ${sec(Math.max(s.ms, s.max_ms))} secondes`;
  return `${sec(s.ms)} seconde${s.ms >= 2000 ? 's' : ''}`;
}

// Les briques par défaut, et les 3 modèles prêts (maquette d1).
export const seqSon = (sound: SeqSound = 'bip'): SeqStep => ({ kind: 'son', sound });
export const seqWait = (ms = 1000, max_ms?: number): SeqStep => (max_ms !== undefined ? { kind: 'attente', ms, max_ms } : { kind: 'attente', ms });
export const seqSignal = (stimuli: Stimuli = { arrows: ['left', 'up', 'right'] }, show_ms = 1000, beep = false): SeqStep => ({ kind: 'signal', stimuli, show_ms, beep });
const seqOf = (steps: SeqStep[]): Sequence => ({ steps, repeat: { mode: 'tours', value: 20 }, shuffle: false, countdown: true, countdown_each: true });
export const SEQ_MODELS: { key: string; name: string; text: string; make: () => Sequence }[] = [
  { key: 'depart', name: 'Départ et lecture', text: 'Bip, 2 s de noir, un chiffre parmi 1, 3 ou 5, 1 s de noir, une flèche. On part au bip, on lit, on suit la flèche.',
    make: () => seqOf([seqSon('bip'), seqWait(2000), seqSignal({ numbers: { mode: 'select', selected: [1, 3, 5] } }), seqWait(1000), seqSignal({ arrows: ['left', 'up', 'right'] })]) },
  { key: 'couleur', name: 'Couleur puis direction', text: 'Sifflet, une couleur, une flèche, puis une attente au hasard entre 1 et 3 s : impossible d’anticiper.',
    make: () => seqOf([seqSon('sifflet'), seqSignal({ colors: ['rouge', 'bleu', 'jaune'] }), seqWait(500), seqSignal({ arrows: ['left', 'right'] }), seqWait(1000, 3000)]) },
  { key: 'systeme', name: 'Annonce du système', text: 'Sifflet, un signe de l’équipe (le nom du système après 1 s), 3 s pour se placer.',
    make: () => seqOf([seqSon('sifflet'), seqSignal({ signs: 'team', sign_names: true }, 2000), seqWait(3000)]) },
];
/** « Partir de zéro » : une flèche, une attente. */
export const blankSequence = (): Sequence => seqOf([seqSignal({ arrows: ['left', 'up', 'right'] }), seqWait(1000)]);

function readStep(v: unknown): SeqStep | null {
  const s = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  if (s.kind === 'son') return { kind: 'son', sound: SEQ_SOUNDS.some(x => x.id === s.sound) ? s.sound as SeqSound : 'bip' };
  if (s.kind === 'attente') {
    const ms = num(s.ms, 1000, WAIT_MIN, WAIT_MAX);
    return typeof s.max_ms === 'number' && Number.isFinite(s.max_ms) ? { kind: 'attente', ms, max_ms: num(s.max_ms, ms, WAIT_MIN, WAIT_MAX) } : { kind: 'attente', ms };
  }
  if (s.kind === 'signal') return { kind: 'signal', stimuli: readStimuli(s.stimuli), show_ms: num(s.show_ms, 1000, SHOW_MIN, SHOW_MAX), beep: bool(s.beep, false) };
  return null;
}
/** Une séquence relue sans confiance (jsonb) ; rien de lisible : « Partir de zéro ». */
export function readSequence(v: unknown): Sequence {
  const c = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const steps = (Array.isArray(c.steps) ? c.steps : []).slice(0, SEQ_MAX).map(readStep).filter((x): x is SeqStep => !!x);
  if (!steps.length) return blankSequence();
  const r = (c.repeat ?? {}) as Record<string, unknown>;
  const mode = r.mode === 'minutes' ? 'minutes' : 'tours';
  return { steps, repeat: { mode, value: num(r.value, mode === 'minutes' ? 3 : 20, 1, mode === 'minutes' ? 60 : 500) }, shuffle: bool(c.shuffle, false), countdown: bool(c.countdown, true), countdown_each: bool(c.countdown_each, true) };
}
